import { PhaserGamePad } from "src/games/shared/Gamepad";
import { NPC } from '../objects/NPC';
import TranslateApi from "src/utils/TranslateHelper";
import { FoodItems } from "../objects/Food";
import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { EndGameScene } from "src/games/DinerGame/scenes/EndGameScene";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    foodWidth: number = 56;
    foodHeight: number = 36;
    groundLayer: Phaser.Tilemaps.DynamicTilemapLayer;
    objectLayer: Phaser.Tilemaps.DynamicTilemapLayer;
    map: Phaser.Tilemaps.Tilemap;
    tiles: Phaser.Tilemaps.Tileset;
    player: Phaser.Physics.Arcade.Sprite;
    speed: number = 200;
    generalSize: number = 64;
    gamePad: PhaserGamePad;
    npcArea: NPC[] = [];
    foodItems: FoodItems[] = [];
    serving: boolean = false;
    currentFood: Phaser.GameObjects.Image;
    count: number;
    foodTextures: string[] = ["food1", "food2", "food3", "food4", "food5", "food6"];
    timer: Phaser.Time.TimerEvent;
    timeElapsed: Phaser.GameObjects.Text;
    currentTime: number = 0;
    score: number = 0;
    break: boolean = false;
    stars: Phaser.GameObjects.Image[] = [];
    currentFoodExists: boolean = false;
    droppedScore: boolean;
    finishModal: PhaserModal;

    xVel: number = 0;
    yVel: number = 0;

    constructor() {
        super({
            key: "GameScene"
        });
    }

    preload(): void {
        this.load.image("patron", require("/src/assets/qci/atlas/dinerGame/leftside.png"));
        this.load.tilemapTiledJSON("map", require("/src/assets/qci/maps/diner.json"));
        this.load.spritesheet('player', require('src/assets/qci/sprites/studentPilot/student_spritemap-64x96.png'), { frameWidth: 64, frameHeight: 96 });
        this.load.image("star", require("/src/assets/qci/sprites/star.png"));
        this.load.image("food1", require("/src/assets/qci/atlas/dinerGame/item__food-1.png"));
        this.load.image("food2", require("/src/assets/qci/atlas/dinerGame/item__food-2.png"));
        this.load.image("food3", require("/src/assets/qci/atlas/dinerGame/item__food-3.png"));
        this.load.image("food4", require("/src/assets/qci/atlas/dinerGame/item__food-4.png"));
        this.load.image("food5", require("/src/assets/qci/atlas/dinerGame/item__food-5.png"));
        this.load.image("food6", require("/src/assets/qci/atlas/dinerGame/item__food-6.png"));
        this.load.image("tiles", require("/src/assets/qci/atlas/dinerGame/diner_game.png"));
        PhaserGamePad.preload(this);
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        bindAllOfThis(this, MainScene.prototype);
        this.timeElapsed = this.add.text(96, 64, "0:00", { fill: "#000000", align: "center" }).setScrollFactor(0).setDepth(7.5).setOrigin().setFontFamily('Russo One, Roboto Mono, Arial').setFontSize(30);
        this.createMap();
        this.createPlayer();
        this.createFoodTable();
        this.createFoods();
        this.createTimer();
        this.createRating();
        this.createCollision();
        this.createCameras();
        this.generateGamepad();
        this.onKeydown(() => {
            if (this.finishModal) {
                this.finishModal.selectLastOption();
            }
        }, 'ENTER', 'SPACE');
    }

    modalPop(text: string, gameover?: boolean): void {
        if(gameover) {
            this.player.disableBody();
        }

        var config = new PhaserModalConfig();
        config.title = text;
        config.buttonConfig.smartSize = true;
        this.finishModal = new PhaserModal(this, 0, 0, [{ id: 1, displayText: TranslateApi.translate("common.done") }], config);
        this.finishModal.setPosition(this.gameWidth / 2 - this.finishModal.width / 2, this.gameHeight / 2 - this.finishModal.height / 2);
        this.finishModal.on('optionClicked', () => {
            this.finishModal.destroy();
            if (gameover) {
                this.input.setDefaultCursor("default");
                this.input.removeAllListeners();
                this.scene.add("EndGameScene", EndGameScene, true);
                this.scene.remove(this);
            }
        });
    }

    createMap() {
        this.map = this.make.tilemap({ key: "map" });
        this.tiles = this.map.addTilesetImage('tiles');
        this.groundLayer = this.map.createDynamicLayer('Background', this.tiles, 0, 0);
        this.objectLayer = this.map.createDynamicLayer('Objects', this.tiles, 0, 0);
    }

    createFoodTable() {
        this.createEatingTable(6, 5, "patron");
        this.createEatingTable(15, 5, "patron");
        this.createEatingTable(5, 9, "patron");
        this.createEatingTable(17, 9, "patron");
        this.createEatingTable(23, 6, "patron");
    }

    createFoods() {
        var i = 0;
        this.map.getObjectLayer("Food").objects.forEach((ob: TiledGameObject) => {
            var food = this.foodTextures[i];
            let foodPlate = this.add.image(ob.x, ob.y, food);
            foodPlate.setOrigin(0);
            foodPlate.setDepth(3);
            foodPlate.setDisplaySize(ob.width, ob.height);
            let rect = new Phaser.Geom.Rectangle(foodPlate.x + 8, foodPlate.y, 32, 96);
            this.foodItems.push(new FoodItems(foodPlate, rect, false, food));
            i++;
        });
    }

    createEatingTable(posX: number, posY: number, texture: string) {
        this.createNPC(posX - 1, posY, texture, false);
        this.createNPC(posX + 3, posY, texture, true);
    }

    createNPC(x: number, y: number, texture: string, flip: boolean) {
        var translateString = "Diner.Phaser.";
        var npcGoodGreeting = ["correct.1", "correct.2", "correct.3", "correct.4"];
        var npcBadGreeting = ["wrong.1", "wrong.2", "wrong.3"];
        npcGoodGreeting = npcGoodGreeting.sort(() => .5 - Math.random());
        npcBadGreeting = npcBadGreeting.sort(() => .5 - Math.random());
        var npc = this.physics.add.staticImage(64 * x + 32, 64 * y + 32, texture);
        // ** This is for the physics/collision bounds **
        npc.setSize(64, 45);
        npc.setOffset(0, 35);

        npc.setFlipX(flip);
        npc.setDisplaySize(64, 64);
        this.physics.add.collider(npc, this.player);
        this.foodTextures = this.scrambleArray(this.foodTextures);
        var food = this.add.image(64 * x + 32, 64 * (y - 1) + 32, this.foodTextures[Phaser.Math.Between(0, this.foodTextures.length - 1)]);
        food.setVisible(false);
        food.setDisplaySize(this.foodWidth, this.foodHeight);
        let rect = new Phaser.Geom.Rectangle(64 * (x - 1) + 32, 64 * (y - 1) + 32, 64 * 2, 64 * 2);
        let npcGoodText = TranslateApi.translate(translateString + npcGoodGreeting[0]);
        let npcBadText = TranslateApi.translate(translateString + npcBadGreeting[0]);
        this.npcArea.push(new NPC(food, npc, rect, null, npcGoodText, npcBadText, 0));
    }

    addTextBlurb(x: number, y: number, text: string): Phaser.GameObjects.Container {
        var padding = 5;
        var fontSize = 14;
        var borderWidth = 2;
        var borderColor = 0xff0000;
        var textBox = this.add.container(x, y);
        var textStyle = { fontFamily: 'Roboto Mono, Arial', color: "#ffffff", fontSize: fontSize, padding: 10 };

        var textObject = this.add.text(padding + borderWidth, padding + borderWidth, text, textStyle);
        textObject.setWordWrapWidth(textObject.width + padding, true);
        textBox.setSize(textObject.displayWidth + padding + borderWidth, textObject.displayHeight + padding + borderWidth);

        // Add text box background
        var textBoxBackground = this.add.image(0, 0, "pixel");
        textBoxBackground.setOrigin(0);
        textBoxBackground.setDisplaySize(textBox.displayWidth, textBox.displayHeight);
        textBoxBackground.setTint(0x000000);
        textBoxBackground.setPosition(borderWidth + padding + textStyle.padding, borderWidth + padding + textStyle.padding);

        // Add text box border
        var textBoxBorder = this.add.image(0, 0, "pixel");
        textBoxBorder.setOrigin(0);
        textBoxBorder.setDisplaySize(textBoxBackground.displayWidth + 2 * borderWidth, textBoxBackground.displayHeight + 2 * borderWidth);
        textBoxBorder.setTint(borderColor);
        textBoxBackground.setPosition(borderWidth, borderWidth);

        textBox.add(textBoxBorder);
        textBox.add(textBoxBackground);
        textBox.add(textObject);

        return textBox;
    }

    createCameras() {
        this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.cameras.main.startFollow(this.player);
    }

    createCollision() {
        this.physics.add.collider(this.player, this.groundLayer);
        this.physics.add.collider(this.player, this.objectLayer);
        this.objectLayer.forEachTile((tile) => {
            var tileWorldPos = this.objectLayer.tileToWorldXY(tile.x, tile.y);
            var collisionGroup = this.tiles.getTileCollisionGroup(tile.index);
            if (!collisionGroup || collisionGroup["objects"].length === 0) { return; }
            // The group will have an array of objects - these are the individual collision shapes
            var objects = collisionGroup["objects"];
            for (var i = 0; i < objects.length; i++) {
                var object = objects[i];
                var objectX = tileWorldPos.x + object.x;
                var objectY = tileWorldPos.y + object.y;
                // Add collider for rectangles
                if (object.rectangle) {
                    var gameObject = new Phaser.GameObjects.Zone(this, objectX, objectY, object.width, object.height);
                    this.physics.add.existing(gameObject, true);
                    this.physics.add.collider(this.player, gameObject);
                }
            }
        });
    }

    createTimer() {
        this.timer = this.time.addEvent({ delay: 10000000, timeScale: 1.0 });
        var clock = this.add.image(this.timeElapsed.x, this.timeElapsed.y, "pixel");
        clock.setDisplaySize(this.timeElapsed.displayWidth + 30, this.timeElapsed.displayHeight + 20).setDepth(6.5).setScrollFactor(0);
        var clockBorder = this.add.image(clock.x, clock.y, "pixel");
        clockBorder.setDisplaySize(clock.displayWidth + 10, clock.displayHeight + 10).setDepth(6).setTint(0x000000).setScrollFactor(0);
    }

    createPlayer() {
        this.player = this.physics.add.sprite(64 * 11 + 32, 64 * 6.5 + 32, "player");
        // ** This is for the physics/collision bounds - if you want to see a visual, set debug: true in Main.tsx**
        this.player.setSize(50, 80);
        this.player.setOffset(6, 15);
        this.player.setFrame(4);
        this.physics.world.setBounds(0, 0, 64 * 30, 64 * 14);
        this.player.setCollideWorldBounds(true);

        //  Player animations, turning, walking left and walking right.
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('player', { start: 0, end: 3 }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'turn',
            frames: [{ key: 'player', frame: 4 }],
            frameRate: 20
        });

        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('player', { start: 5, end: 8 }),
            frameRate: 10,
            repeat: -1
        });
    }


    createRating() {
        var xCoor = this.gameWidth - 5 * 50;
        var yCoor = 32;
        var score = this.add.group();
        for (var i = 0; i < 5; i++) {
            var star = this.add.image(0, 0, "star");
            star.setOrigin(0);
            star.setDisplaySize(48, 48);
            star.setScrollFactor(0);
            score.add(star);
            this.stars.push(star);
        }

        score.setDepth(5, 0);

        Phaser.Actions.GridAlign(score.getChildren(), {
            x: xCoor,
            y: yCoor,
            width: 5,
            height: 1,
            cellHeight: 50,
            cellWidth: 50,
        });
    }

    updateNPC() {
        this.npcArea.forEach((npc: NPC) => {
            if (!npc.served) {
                let doesIntersect = Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), npc.rect);
                if (doesIntersect) {
                    npc.food.setVisible(true);
                }
                else {
                    npc.food.setVisible(false);
                }
            }
        });
    }

    updateFoodPosition() {
        this.foodItems.forEach((food: FoodItems) => {
            if (food.follow) {
                this.currentFood.y = this.player.y + 20;
                if (this.player.body.velocity.x < 0) {
                    this.currentFood.x = this.player.x - 32;
                }
                else if (this.player.body.velocity.x > 0) {
                    this.currentFood.x = this.player.x + 32;
                }
                else {
                    this.currentFood.x = this.player.x;
                }
            }
        });
    }

    increaseTime() {
        var time = Math.trunc(this.timer.getElapsedSeconds());
        var minute: number = Math.floor(time / 60);
        var second: number = Math.floor(time % 60);
        if (time % 45 === 1) {
            this.droppedScore = false;
            this.timeElapsed.setColor("0x000000");
        }
        if (time !== 0 && time % 45 === 0 && !this.droppedScore) {
            this.timeElapsed.setColor('#ff0000');
            this.score--;
            if (this.score >= 0 && this.score <= 4) {
                this.stars[this.score].setTint(0xffffff);
            }
            this.droppedScore = true;
        }

        if (second < 10) {
            this.timeElapsed.setText(minute.toString() + ":0" + second.toString());
        }
        else {
            this.timeElapsed.setText(minute.toString() + ":" + second.toString());
        }
    }

    /**
   * Shuffle the candidates so they're not in the same order every time
   * https://stackoverflow.com/questions/6274339/how-can-i-shuffle-an-array
   */
    scrambleArray(array) {
        var j, x, i;
        for (i = array.length - 1; i > 0; i--) {
            j = Math.floor(Math.random() * (i + 1));
            x = array[i];
            array[i] = array[j];
            array[j] = x;
        }
        return array;
    }

    fadeOutFeedback(blurb, npc, positive): void {
        this.tweens.add({
            targets: blurb,
            x: npc.rect.x,
            duration: 2000,
            ease: "Linear",
            onComplete: () => {
                this.tweens.add({
                    targets: blurb,
                    duration: 500,
                    x: blurb.x,
                    ease: "Linear",
                    alpha: {
                        getStart: () => 1,
                        getEnd: () => 0,
                    },
                    onComplete: () => {
                        blurb.destroy();
                        if (!positive) {
                            npc.served = null;
                        }
                    },
                });
            }
        });
    }

    update(): void {
        this.updatePlayerMovement();
        this.updateNPC();
        this.updateFoodPosition();
        this.increaseTime();
    }

    /**
       * GamePad/Player Movement
       */
    generateGamepad(): void {
        this.gamePad = new PhaserGamePad(this);
        this.gamePad.on("keydown_a", this.aDown);
        this.gamePad.on("keydown_b", this.bDown);
        this.gamePad.on("keyup_a", this.stopA);
    }

    bDown() {
        if (this.finishModal) {
            this.finishModal.selectLastOption();
            return;
        }

        if (this.serving) {
            this.npcArea.forEach((npc: NPC) => {
                if (!this.break) {
                    let doesIntersect = Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), npc.rect);
                    if (doesIntersect
                        && npc.food.texture === this.currentFood.texture
                        && !npc.served) {
                        npc.food.setVisible(true);
                        npc.served = true;
                        npc.time = this.currentTime;
                        if (npc.npc.flipX) {
                            npc.food.setPosition(npc.food.x - 64, npc.food.y + 64);
                        }
                        else {
                            npc.food.setPosition(npc.food.x + 64, npc.food.y + 64);
                        }
                        this.currentFood.destroy();
                        this.serving = false;
                        if (this.score >= 0 && this.score <= 4) {
                            this.stars[this.score].setTint(0xffff00);
                        }
                        this.score++;
                        // Show good feedback
                        var blurb = this.addTextBlurb(npc.rect.x, npc.rect.y - 20, npc.goodText);
                        this.fadeOutFeedback(blurb, npc, true);
                        this.break = true;
                    }
                    if (doesIntersect
                        && npc.food.texture !== this.currentFood.texture
                        && !npc.served) {
                        npc.served = false;
                        npc.time = this.currentTime;
                        this.score--;
                        if (this.score >= 0 && this.score <= 4) {
                            this.stars[this.score].setTint(0xffffff);
                        }
                        // Show bad feedback
                        blurb = this.addTextBlurb(npc.rect.x, npc.rect.y - 20, npc.badText);
                        this.fadeOutFeedback(blurb, npc, false);
                    }
                }
            });
            this.break = false;
        }
        this.foodItems.forEach((food: FoodItems) => {
            let doesIntersect = Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), food.rect);
            if (doesIntersect) {
                if (this.currentFoodExists) {
                    this.currentFood.destroy();
                    this.currentFoodExists = false;
                }
                this.currentFood = this.add.image(0, 0, food.foodTexture);
                this.currentFood.setDisplaySize(this.foodWidth, this.foodHeight);
                this.serving = true;
                this.currentFoodExists = true;
                food.follow = true;
            }
        });

        if (this.npcArea[0].served && this.npcArea[1].served && this.npcArea[2].served
            && this.npcArea[3].served && this.npcArea[4].served && this.npcArea[5].served
            && this.npcArea[6].served && this.npcArea[7].served && this.npcArea[8].served
            && this.npcArea[9].served) {
            if (this.score >= 5) {
                this.score = 5;
            }
            if (this.score <= 0) {
                this.score = 0;
            }
            var doneText = TranslateApi.translate("Diner.Phaser.doneText") + "\n";
            var scoreText = TranslateApi.translate("Diner.Phaser.score") + ": ";
            this.modalPop(doneText + scoreText + this.score, true);
            this.timer.paused = true;
        }
    }

    aDown() {
        if (this.finishModal) {
            this.finishModal.selectLastOption();
            return;
        }

        // Turn on Turbo speed
        this.speed = 300;
    }

    stopA() {
        // Turn off Turbo speed
        this.speed = 200;
    }

    updatePlayerMovement(): void {
        this.xVel = 0;
        this.yVel = 0;

        if (this.gamePad.keys.left.isDown) {
            this.xVel += -this.speed;
        }
        if (this.gamePad.keys.right.isDown) {
            this.xVel += this.speed;
        }
        if (this.gamePad.keys.down.isDown) {
            this.yVel += this.speed;
        }
        if (this.gamePad.keys.up.isDown) {
            this.yVel += -this.speed;
        }

        this.player.setVelocity(this.xVel, this.yVel);

        // Play walking animations
        if (this.xVel > 0) {
            this.player.anims.play('right', true);
        }
        else if (this.xVel < 0) {
            this.player.anims.play('left', true);
        }
        else {
            this.player.anims.play('turn');
        }
    }

    onKeydown(event: Function, ...keys: string[]): void {
        keys.forEach((key: string) => {
            this.input.keyboard.on('keydown_' + key, event);
        });
    }



}



// WEBPACK FOOTER //
// ./src/games/DinerGame/scenes/MainScene.ts